export default class JobQuiz {
  #form;
  #questions;
  #progress;
  #loading;
  #hiddenClass = 'u-hidden';

  constructor(form) {
    this.#form = form;
    this.#questions = Array.from(form.querySelectorAll('.js-form-question'));
    this.#progress = document.querySelector('.js-quiz-progress');
    this.#loading = document.querySelector('.js-quiz-loading');
    this.#init();
  }

  #init() {
    this.#showOnly(0);
    this.#bindValidateButtons();
    this.#bindBackButtons();
    this.#bindSubmitLoader();
    this.#nestConditionalFields();
    this.#initConditionalFields();
  }

  #nestConditionalFields() {
    this.#form.querySelectorAll('.js-conditional-logic-parent').forEach(parent => {
      const field = parent.querySelector('.js-form-field[data-conditional-logic]');
      if (!field) return;

      const logic = JSON.parse(field.getAttribute('data-conditional-logic'));
      const condition = logic.flat().find(c => c.operator === '==');
      if (!condition) return;

      const ref = document.getElementById(condition.field);
      if (!ref) return;

      const radio = this.#form.querySelector(`input[name="${ref.name}"][value="${condition.value}"]`);
      const item = radio?.closest('.c-form-optionlist__item');
      if (item) item.append(parent);
    });
  }

  #showOnly(index, direction = 0) {
    this.#questions.forEach((q, i) => q.classList.toggle(this.#hiddenClass, i !== index));
    this.#progress?.style.setProperty('--progress', `${((index + 1) / this.#questions.length) * 100}%`);

    if (direction !== 0) {
      const el = this.#questions[index];
      el.style.setProperty('--quiz-slide-from', `${direction * 48}px`);
      el.classList.remove('is-animating');
      void el.offsetWidth;
      el.classList.add('is-animating');
    }
  }

  #bindValidateButtons() {
    this.#questions.forEach((question, index) => {
      question.querySelector('[data-quiz-validate]')
        ?.addEventListener('click', () => this.#advance(index));
    });
  }

  #bindBackButtons() {
    this.#questions.forEach((question, index) => {
      question.querySelector('[data-quiz-back]')
        ?.addEventListener('click', () => this.#showOnly(index - 1, -1));
    });
  }

  #bindSubmitLoader() {
    const submitBtn = this.#form.querySelector('[type="submit"]');
    const callbackName = submitBtn?.dataset.callback;

    const showLoader = () => {
      this.#form.closest('.o-quiz__content')?.classList.add(this.#hiddenClass);
      this.#loading?.classList.remove(this.#hiddenClass);
      setTimeout(() => this.#form.submit(), 2000);
    };

    if (callbackName) {
      window[callbackName] = showLoader;
    } else {
      submitBtn?.addEventListener('click', (e) => { e.preventDefault(); showLoader(); });
    }
  }

  #advance(fromIndex) {
    if (!this.#isValid(fromIndex)) {
      this.#showTextErrors(fromIndex);
      return;
    }
    const next = fromIndex + 1;
    if (next < this.#questions.length) {
      this.#showOnly(next, 1);
      this.#questions[next].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  #showTextErrors(index) {
    const question = this.#questions[index];
    question.querySelectorAll(
      'input[required]:not([disabled]):not([type="radio"]):not([type="hidden"]), textarea[required]:not([disabled])'
    ).forEach(input => {
      if (input.value.trim()) return;

      const field = input.closest('.js-form-field');
      if (!field) return;

      const errorId = `${field.dataset.prefix ?? ''}error`;
      if (document.getElementById(errorId)) return;

      input.closest('.c-form-input')?.classList.add('c-form-input--error');
      input.setAttribute('aria-invalid', 'true');

      const error = document.createElement('div');
      error.className = 'c-form-note c-form-note--error';
      error.id = errorId;
      error.innerHTML = '<p class="c-form-note__text">Ce champ est obligatoire.</p>';
      field.querySelector('.c-form-field__control')?.before(error);

      input.addEventListener('input', () => {
        error.remove();
        input.closest('.c-form-input')?.classList.remove('c-form-input--error');
        input.removeAttribute('aria-invalid');
      }, { once: true });
    });
  }

  #isValid(index) {
    const question = this.#questions[index];

    // Vérifie que chaque groupe de radios obligatoires a une sélection
    const radioNames = new Set(
      Array.from(question.querySelectorAll('input[type="radio"]:not([disabled])'))
        .map(r => r.name)
    );
    for (const name of radioNames) {
      if (!question.querySelector(`input[name="${name}"]:checked`)) return false;
    }

    // Vérifie les champs texte obligatoires visibles
    for (const input of question.querySelectorAll(
      'input[required]:not([disabled]):not([type="radio"]):not([type="hidden"]), textarea[required]:not([disabled])'
    )) {
      if (!input.value.trim()) return false;
    }

    return true;
  }

  #initConditionalFields() {
    this.#form.querySelectorAll('.js-form-field[data-conditional-logic]').forEach(field => {
      const logic = JSON.parse(field.getAttribute('data-conditional-logic'));
      const parent = field.closest('.js-conditional-logic-parent') ?? field;
      const update = () => this.#toggleConditional(field, logic, parent);

      this.#extractFieldIds(logic).forEach(fieldId => {
        // Le champ référencé est un hidden input ; on écoute les radios du même name
        const ref = document.getElementById(fieldId);
        if (!ref) return;
        const targets = ref.type === 'hidden'
          ? document.querySelectorAll(`input[name="${ref.name}"][type="radio"]`)
          : [ref];
        targets.forEach(t => t.addEventListener('change', update));
      });

      update();
    });
  }

  #extractFieldIds(logic) {
    const ids = new Set();
    const walk = node => Array.isArray(node) ? node.forEach(walk) : node?.field && ids.add(node.field);
    walk(logic);
    return ids;
  }

  #toggleConditional(field, logic, parent) {
    const show = this.#evalLogic(logic);
    parent.classList.toggle(this.#hiddenClass, !show);
    field.querySelectorAll('input, textarea, select').forEach(input => {
      input.disabled = !show;
      if (!show) input.setAttribute('aria-disabled', 'true');
      else input.removeAttribute('aria-disabled');
    });
  }

  #evalLogic(logic) {
    return logic.some(group =>
      group.every(({ field: fieldId, operator, value }) => {
        const ref = document.getElementById(fieldId);
        if (!ref) return false;
        const current = ref.type === 'hidden'
          ? document.querySelector(`input[name="${ref.name}"]:checked`)?.value ?? ''
          : ref.value;
        switch (operator) {
          case '==': return current == value;
          case '!=': return current != value;
          default: return false;
        }
      })
    );
  }
}
